import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, computed, input } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { type PaginatorState } from 'primeng/paginator';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, type TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { IndicadorPendencia } from '../mapa-pendencias/indicador-pendencia.model';
import { PendenciaLinha } from '../mapa-pendencias/pendencia-linha.model';

type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';

/** Função opcional que carrega todas as linhas do indicador (todas as páginas) para exportação completa. */
export type ProvedorExportacaoPendencias = () => Observable<PendenciaLinha[]>;

@Component({
    selector: 'app-detalhamento-pendencias',
    standalone: true,
    imports: [CommonModule, ButtonModule, CardContent, CustomTableComponent],
    templateUrl: './detalhamento-pendencias.html',
    styleUrl: './detalhamento-pendencias.scss'
})
export class DetalhamentoPendencias {
    indicador = input<IndicadorPendencia | null>(null);
    linhas = input<PendenciaLinha[]>([]);
    carregando = input(false);
    rotuloVoltar = input('Voltar ao mapa');
    voltarNoCard = input(false);
    colunaDetalhe = input<string | null>(null);
    colunaUf = input('UF');
    colunaIdentificador = input('CRM');
    colunaNome = input('NOME');
    colunaSituacao = input('SITUAÇÃO');

    /** Quando verdadeiro, exibe o alternador Tabela/Cards e permite a visualização em card. */
    permitirVisualizacaoCard = input(false);

    /**
     * Provedor opcional que carrega TODAS as linhas do indicador (todas as páginas) para a
     * exportação CSV completa. Quando ausente, exporta apenas as linhas da página atual.
     */
    provedorExportacao = input<ProvedorExportacaoPendencias | null>(null);
    /** Nome base do arquivo exportado; quando ausente, deriva do título do indicador. */
    nomeArquivoExportacao = input<string | null>(null);

    first = input(0);
    rows = input(100);
    totalRecords = input(0);

    @Output() voltar = new EventEmitter<void>();
    @Output() paginaAlterada = new EventEmitter<PaginatorState>();

    readonly tabelaMetadata = computed<TableMetadata>(() => {
        const ind = this.indicador();
        const isRne = ind?.id === 'rne-vencendo' || ind?.id === 'rne-vencido';
        const headers: TableMetadata['headers'] = {};
        if (isRne) {
            headers['crm'] = { type: 'text', value: this.colunaIdentificador(), hidden: false, default: '-' };
            headers['nome'] = { type: 'text', value: this.colunaNome(), hidden: false, default: '-' };
            headers['vencimento'] = { type: 'text', value: 'DT. VENCIMENTO', hidden: false, default: '-' };
            return { title: 'nome', headers };
        }
        if (this.colunaUf()) headers['uf'] = { type: 'text', value: this.colunaUf(), hidden: false, default: '-' };
        headers['crm'] = { type: 'text', value: this.colunaIdentificador(), hidden: false, default: '-' };
        headers['nome'] = { type: 'text', value: this.colunaNome(), hidden: false, default: '-' };
        if (this.colunaSituacao()) headers['situacao'] = { type: 'tag', value: this.colunaSituacao(), hidden: false, default: '-' };
        if (this.colunaDetalhe()) headers['detalhe'] = { type: 'text', value: this.colunaDetalhe()!, hidden: false, default: '-' };
        if (this.linhas().some((l) => !!l.vencimento)) headers['vencimento'] = { type: 'text', value: 'VENCIMENTO', hidden: false, default: '-' };
        return { title: 'nome', tag: this.colunaSituacao() ? 'situacao' : undefined, headers };
    });

    readonly dadosTabela = computed(() => this.linhas().map((l) => this.mapLinha(l)));

    readonly nomeArquivoCsv = computed(() => {
        const base = this.nomeArquivoExportacao()?.trim() || this.indicador()?.titulo || 'detalhamento';
        return `${base}-${this.dataArquivo()}`;
    });

    /** Provedor no formato do app-custom-table (todas as páginas), quando há `provedorExportacao`. */
    readonly exportProviderTabela = computed<(() => Promise<Record<string, unknown>[]>) | null>(() => {
        const provedor = this.provedorExportacao();
        if (!provedor) {
            return null;
        }
        return () => firstValueFrom(provedor().pipe(map((linhas) => (linhas ?? []).map((l) => this.mapLinha(l)))));
    });

    /** Converte uma linha de pendência para o formato de dados do app-custom-table (situação como tag). */
    private mapLinha(l: PendenciaLinha): Record<string, unknown> {
        return {
            uf: l.uf ?? '',
            crm: l.crm ?? '',
            nome: l.nome ?? '',
            situacao: { value: this.obterRotuloSituacao(l), severity: this.obterSeveridadeSituacao(l) },
            detalhe: l.detalhe ?? '',
            vencimento: l.vencimento ?? ''
        };
    }

    voltarParaMapa(): void {
        this.voltar.emit();
    }

    private dataArquivo(): string {
        const agora = new Date();
        const ano = agora.getFullYear();
        const mes = String(agora.getMonth() + 1).padStart(2, '0');
        const dia = String(agora.getDate()).padStart(2, '0');
        return `${ano}-${mes}-${dia}`;
    }

    onPageChange(event: PaginatorState): void {
        this.paginaAlterada.emit(event);
    }

    obterSeveridadeSituacao(linha: PendenciaLinha): TagSeverity {
        const codigo = (linha.codigoSituacao ?? '').trim().toUpperCase();
        const situacao = (linha.situacao ?? '').trim().toUpperCase();

        if (codigo === 'A' || situacao.includes('ATIVO')) {
            return 'success';
        }

        if (codigo === 'T' || situacao.includes('TRANSFERIDO')) {
            return 'info';
        }

        if (codigo === 'S' || codigo === 'X' || situacao.includes('SUSPENSO') || situacao.includes('AFASTADO')) {
            return 'warn';
        }

        if (['C', 'F', 'L', 'I'].includes(codigo) || situacao.includes('CASSADO') || situacao.includes('FALECIDO') || situacao.includes('CANCELADO') || situacao.includes('INTERD')) {
            return 'danger';
        }

        return 'secondary';
    }

    obterRotuloSituacao(linha: PendenciaLinha): string {
        return linha.situacao?.trim() || linha.codigoSituacao?.trim() || 'Não informado';
    }

    obterIconeIndicador(grupo?: string | null): string {
        switch (grupo) {
            case 'Dados pessoais':
                return 'pi pi-user';
            case 'Registro profissional':
                return 'pi pi-id-card';
            case 'Formação':
                return 'pi pi-graduation-cap';
            case 'Endereço':
                return 'pi pi-map-marker';
            case 'Diplomas':
                return 'pi pi-file';
            case 'Financeiro':
                return 'pi pi-money-bill';
            case 'Idade':
                return 'pi pi-calendar';
            case 'RNE':
                return 'pi pi-globe';
            case 'Evolucao':
                return 'pi pi-chart-line';
            case 'Perfil':
                return 'pi pi-users';
            case 'Registro':
                return 'pi pi-id-card';
            case 'Transferencia':
                return 'pi pi-arrow-right-arrow-left';
            case 'Situacao':
                return 'pi pi-verified';
            case 'Operacional':
                return 'pi pi-briefcase';
            case 'Qualidade':
                return 'pi pi-check-circle';
            case 'Especialidade':
                return 'pi pi-star';
            default:
                return 'pi pi-exclamation-triangle';
        }
    }
}
